/**
 * RaroBrandContext.jsx — SINGLE source of truth for every visible
 * string / image / colour on the Raro site.  Fetches /api/raro/brand
 * ONCE at first paint and shares it via React context.  Every child
 * page renders `{brand.name}`, `{brand.address.full}`, `{brand.contact.phone_display}`,
 * `{brand.brand.hero_image_url}` — no hardcoded strings anywhere.
 *
 * If the owner edits any field via `/api/raro/brand` (PUT) the whole
 * site updates on the next page refresh.
 */
import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { resolveRestaurantBusinessId } from "@/lib/hostRegistry";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

/** Which business_id are we serving?  Resolved once at boot from the
 *  hostname → business_id map in hostRegistry.  Falls back to the
 *  Raro demo record for the local pod-URL preview and for tests.  Can
 *  be overridden per-render by passing `businessId` to <RaroBrandProvider/>. */
function resolveBusinessIdFromHost() {
  if (typeof window === "undefined") return "biz_raro_demo";
  const host = (window.location.hostname || "").toLowerCase();
  return resolveRestaurantBusinessId(host) || "biz_raro_demo";
}

/** Minimal safe fallback while /api/raro/brand loads or fails.  All
 *  strings intentionally empty so the visitor sees a subtle spinner /
 *  blank line, never a stale name. */
const EMPTY_BRAND = {
  id: "biz_raro_demo",
  name: "",
  trading_name: "",
  legal_name: "",
  dba_name: "",
  abn: "",
  abn_verified: false,
  industry: "restaurant",
  category: "Restaurant",
  sub_category: "",
  niche: "restaurant",
  sub_niche: "",
  template_id: "",
  template_version: "",
  cuisine: "",
  sub_cuisine: "",
  cuisine_tags: [],
  offering_type: [],
  signature_dishes: [],
  menu_size: 0,
  tagline: "",
  about_short: "",
  about_long: "",
  opening_year: null,
  seats: 0,
  bar_seats: 0,
  private_dining_seats: 0,
  price_range: "$$",
  service_options: [],
  payment_methods: [],
  parking: "",
  accessibility: [],
  wifi_available: false,
  kid_friendly: false,
  dog_friendly: false,
  byo: false,
  corkage_fee: 0,
  dietary_tags: [],
  allergen_info: "",
  primary_domain: "",
  domains: [],
  landmarks: [],
  nearby_transit: [],
  neighbourhood: "",
  hero_headline: "",
  hero_subheadline: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: [],
  schema_type: "Restaurant",
  aggregate_rating: null,
  review_count: 0,
  google_place_id: "",
  address: { line1: "", line2: "", suburb: "", state: "", postcode: "", country: "AU", full: "", google_maps: "" },
  contact: { email: "", phone_landline: "", phone_mobile: "", phone_display: "", phone_tel: "" },
  owner:   { name: "", email: "", mobile: "" },
  social:  { instagram: "", instagram_handle: "", facebook: "", facebook_handle: "", tiktok: "", youtube: "", google_business: "", x_twitter: "", linkedin: "" },
  booking: { enabled: true, min_notice_hours: 2, max_party: 20, deposit_required: false, deposit_amount: 0, booking_email: "" },
  compliance: { food_licence_no: "", food_licence_expiry: null, liquor_licence_no: "", liquor_licence_type: "", liquor_licence_expiry: null,
                capacity_limit: 0, health_star_rating: null,
                public_liability_insurer: "", public_liability_policy_no: "", public_liability_expiry: null },
  brand:   { logo_light_url: "", logo_dark_url: "", logo_mono_url: "", hero_image_url: "", story_image_url: "", favicon_url: "",
             gallery: [],
             color_primary: "#a01e28", color_gold: "#c9a24b", color_cream: "#fdf6ec", color_ink: "#1a1210", color_accent: "",
             font_display: "Georgia, 'Times New Roman', serif", font_body: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
  hours:   { raw: {}, lines: [] },
};

const RaroBrandContext = createContext({
  brand: EMPTY_BRAND, loading: true, error: null, reload: () => {}, businessId: "biz_raro_demo",
});

export function RaroBrandProvider({ children, businessId }) {
  const [resolvedId] = useState(() => businessId || resolveBusinessIdFromHost());
  const [brand, setBrand] = useState({ ...EMPTY_BRAND, id: resolvedId });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    // Prefer the generic /api/restaurant/:id/brand — every restaurant
    // template hits the same endpoint with its own id.  Any legacy
    // integration that still calls /api/raro/brand remains supported
    // server-side.
    axios.get(`${API}/api/restaurant/${resolvedId}/brand`)
      .then((r) => { setBrand(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [resolvedId]);

  // Push CSS vars so any component can consume via var(--raro-red) etc.
  useEffect(() => {
    if (!brand?.brand) return;
    const s = document.documentElement.style;
    s.setProperty("--raro-red",   brand.brand.color_primary);
    s.setProperty("--raro-gold",  brand.brand.color_gold);
    s.setProperty("--raro-cream", brand.brand.color_cream);
    s.setProperty("--raro-ink",   brand.brand.color_ink);
  }, [brand]);

  return (
    <RaroBrandContext.Provider value={{ brand, loading, error, reload: load, businessId: resolvedId }}>
      {children}
    </RaroBrandContext.Provider>
  );
}

export function useRaroBrand() {
  return useContext(RaroBrandContext);
}
